import React, { FunctionComponent } from 'react'
import { Text as RNText, StyleSheet, View } from 'react-native'

import { LinearGradient } from 'expo-linear-gradient'
import { capitalize } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  BADGE_HEIGHT,
  BADGE_ICON_SIZE,
  padding as badgePadding,
  BadgeShape,
  BadgeVariant,
  borderRadius,
} from '../../badge/constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { AiLinearGradient } from '../aiLinearGradient/AiLinearGradient'

const BadgeContainer = styled(View)<{
  backgroundColor: string
  borderRadius: number
  height?: number
}>(({ backgroundColor, borderRadius, height }) => ({
  backgroundColor,
  borderRadius,
  ...(height !== undefined && { height }),
}))

const GradientBadgeContainer = styled(LinearGradient)<{
  borderRadius: number
  height: number
}>(({ borderRadius, height }) => ({
  borderRadius,
  height,
}))

const TextContainer = styled(View)<{
  marginLeft: number
  marginRight: number
}>(({ marginLeft, marginRight }) => ({
  marginLeft,
  marginRight,
  justifyContent: 'center',
}))

const StyledText = styled(RNText)<{ bold: boolean; color: string; fontSize?: number }>(({ bold, color, fontSize }) => ({
  fontSize: fontSize ?? 12,
  lineHeight: 16,
  fontWeight: bold ? '600' : '500',
  color,
  textAlign: 'center',
}))

export interface IconBadgeProps {
  frontIcon?: React.ReactNode
  backIcon?: React.ReactNode
  text?: React.ReactNode
  variant?: BadgeVariant
  shape?: BadgeShape
  testID?: string
  backgroundColor?: string
  style?: any
  preserveCase?: boolean
  bold?: boolean
  customStyle?: any
  frontIconSize?: number
  backIconSize?: number
  textSizePx?: number
  gradient?: [string, string] | 'ai' // Special case for AI gradient
}

export const IconBadge: FunctionComponent<IconBadgeProps> = (props) => {
  const { colors, isAuroraTheme } = useTheme() as ThemeType
  const {
    frontIcon,
    backIcon,
    text,
    variant,
    shape,
    testID,
    backgroundColor,
    style,
    preserveCase = false,
    bold = false,
    customStyle,
    frontIconSize,
    backIconSize,
    textSizePx,
    gradient,
  } = props

  const hasIcon = Boolean(frontIcon) || Boolean(backIcon)

  const getBadgeColors = () => {
    const badgeTokens = (colors as any).components?.badge
    if (!badgeTokens) {
      return {
        bg: colors.backgroundError || '#FF0000',
        fg: colors.textInverse || '#FFFFFF',
      }
    }

    switch (variant) {
      case BadgeVariant.SUCCESS:
        return { bg: badgeTokens.success.background, fg: badgeTokens.success.text }
      case BadgeVariant.ATTENTION:
        return { bg: badgeTokens.attention.background, fg: badgeTokens.attention.text }
      case BadgeVariant.ALERT:
        return { bg: badgeTokens.alert.background, fg: badgeTokens.alert.text }
      case BadgeVariant.NEUTRAL:
        return { bg: badgeTokens.neutral.background, fg: badgeTokens.neutral.text }
      case BadgeVariant.INFO_PRIMARY:
        return { bg: badgeTokens.infoPrimary.background, fg: badgeTokens.infoPrimary.text }
      case BadgeVariant.INFO_SECONDARY:
        return { bg: badgeTokens.infoSecondary.background, fg: badgeTokens.infoSecondary.text }
      case BadgeVariant.INVERSE:
        return { bg: badgeTokens.inverse.background, fg: badgeTokens.inverse.text }
      case BadgeVariant.NEUTRAL_TRANSPARENT:
        return { bg: badgeTokens.neutralTransparent.background, fg: badgeTokens.neutralTransparent.text }
      case BadgeVariant.LYRA_AI:
        return { bg: badgeTokens.lyraAi.background, fg: badgeTokens.lyraAi.text }
      default:
        return { bg: badgeTokens.neutral.background, fg: badgeTokens.neutral.text }
    }
  }

  const badgeColors = getBadgeColors()
  const bg = backgroundColor ?? badgeColors.bg
  const fg = badgeColors.fg

  const textString = typeof text === 'string' ? text : '' // guarantee string for processing

  let renderedText: string
  if (shape === BadgeShape.RECT) {
    renderedText = textString.toUpperCase()
  } else {
    renderedText = preserveCase ? textString : capitalize(textString)
  }

  const finalText = typeof text === 'string' ? renderedText : text // use original if React node
  const children = (
    <>
      {frontIcon && (
        <View>
          {React.isValidElement(frontIcon)
            ? React.cloneElement(frontIcon as React.ReactElement, {
                width: frontIconSize ?? BADGE_ICON_SIZE,
                height: frontIconSize ?? BADGE_ICON_SIZE,
                fillColor: frontIcon?.props?.fillColor ?? fg,
              })
            : frontIcon}
        </View>
      )}

      {finalText && (
        <TextContainer marginLeft={frontIcon ? 4 : 0} marginRight={backIcon ? 4 : 0}>
          {typeof finalText === 'string' ? (
            <StyledText bold={bold} color={fg} fontSize={textSizePx}>
              {finalText}
            </StyledText>
          ) : (
            finalText
          )}
        </TextContainer>
      )}

      {backIcon && (
        <View>
          {React.isValidElement(backIcon)
            ? React.cloneElement(backIcon as React.ReactElement, {
                width: backIconSize ?? BADGE_ICON_SIZE,
                height: backIconSize ?? BADGE_ICON_SIZE,
                fillColor: backIcon?.props?.fillColor ?? fg,
              })
            : backIcon}
        </View>
      )}
    </>
  )

  if (gradient) {
    const isAiGradient = gradient === 'ai'
    const combinedStyle = [
      styles.container,
      badgePadding[shape ?? BadgeShape.ROUND][hasIcon ? 'icon' : 'text'],
      style,
      customStyle,
    ]

    if (isAiGradient) {
      return (
        <AiLinearGradient
          testID={testID}
          borderRadius={borderRadius[shape ?? BadgeShape.ROUND]}
          height={BADGE_HEIGHT}
          style={StyleSheet.flatten(combinedStyle)}
          rotationAngle={200}
        >
          {children}
        </AiLinearGradient>
      )
    }

    return (
      <GradientBadgeContainer
        testID={testID}
        borderRadius={borderRadius[shape ?? BadgeShape.ROUND]}
        height={BADGE_HEIGHT}
        colors={gradient}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={combinedStyle}
      >
        {children}
      </GradientBadgeContainer>
    )
  }
  return (
    <BadgeContainer
      testID={testID}
      backgroundColor={bg}
      borderRadius={borderRadius[shape ?? BadgeShape.ROUND]}
      height={isAuroraTheme ? undefined : BADGE_HEIGHT}
      style={[styles.container, badgePadding[shape ?? BadgeShape.ROUND][hasIcon ? 'icon' : 'text'], style, customStyle]}
    >
      {children}
    </BadgeContainer>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
